import { useFavouriteProducts } from '@dealerportal/react-helpers';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import classNames from 'classnames';
import { type FC, useMemo } from 'react';
import type { IconBaseProps } from 'react-icons';
import { FaHeart, FaRegHeart } from 'react-icons/fa6';
import { NotificationCounter } from '../../../../../ui-components/src/lib/NotificationCounter';

/**
 * This component displays the number of favorite products.
 * It uses the `useFavouriteProducts` hook to access the favorite products context and retrieve the count.
 * The count is then displayed using the `BetterButton` component, which is customized with a primary button style and an icon of a heart.
 * Clicking on the button triggers the `open` function from the favorite products context.
 *
 * @param className - A string representing the CSS class name for the component.
 * @param labelClassName - A string representing the CSS class name for the label element.
 * @param replaceClass - An object containing key-value pairs to replace specific CSS classes in the `buttonClasses` string.
 * @returns The rendered `FavouritesCount` component.
 */
export const FavouritesCount: FC<{
  className?: string;
  labelClassName?: string;
  iconClassName?: string;
  replaceClass?: Record<string, string>;
  replaceClassIcon?: Record<string, string>;
}> = ({
  className = '',
  labelClassName = 'ui-leading-[5px] ui-min-h-[7px]',
  replaceClass = {},
  iconClassName = '',
  replaceClassIcon = {},
}) => {
  const {
    favCount = 0,
    open = () => {},
    showPip,
    triggerPip,
    clearPip,
  } = useFavouriteProducts();

  const count = useMemo(
    () => (favCount > 9 ? '+9' : favCount.toString()),
    [favCount]
  );

  const handleClick = () => {
    open();
    if (showPip) {
      clearPip(); // Clear pip on click
    }
  };
  // reaplace class for Icon for another files
  const classNameIcon = useMemo(() => {
    let baseIconClass = `!ui-w-5 !ui-h-auto !ui-fill-red-600 ${iconClassName}`;
    Object.entries(replaceClassIcon).forEach(([key, value]) => {
      baseIconClass = baseIconClass.replaceAll(key, value);
    });
    return baseIconClass;
  }, [iconClassName, replaceClassIcon]);

  return (
    <span className="ui-relative ui-inline-flex">
      <BetterButton
        btnStyle="primary"
        attributes={{ onClick: handleClick, type: 'button' }}
        label={count}
        replaceClass={{
          'ui-h-12': 'ui-h-10',
          '!ui-px-6': 'ui-px-3',
          '!ui-bg-[var(--cta-color,#030712)]': '!ui-bg-white',
          'hover:!ui-bg-[var(--hover-color,#111827)]': '!ui-bg-white',
          '!ui-text-white': '!ui-text-black',
          '!ui-border-[var(--cta-color,#030712)]': '!ui-border-[#6B7280]',
          ...replaceClass,
        }}
        className={className}
        labelClassName={labelClassName}
        icon={<FaHeart className={classNames(classNameIcon)} />}
      />
      {showPip && (
        <div className="ui-absolute -ui-top-1 -ui-right-1">
          <NotificationCounter size="3" animate={true} showCount={false} />
        </div>
      )}
    </span>
  );
};

interface HeartProps extends IconBaseProps {
  active?: boolean;
  className?: string;
}

export const HeartIcon = ({
  active = false,
  className,
  ...props
}: HeartProps) => {
  if (active) {
    return (
      <FaHeart
        className={classNames('!ui-fill-red-600', className)}
        {...props}
      />
    );
  }
  return (
    <FaRegHeart
      className={classNames(
        '!ui-fill-[#6B7280] hover:!ui-fill-[#6B7280]',
        className
      )}
      {...props}
    />
  );
};
